import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import type { Database } from '@/integrations/supabase/types';

type AppRole = Database['public']['Enums']['app_role'];

const ROLE_PRIORITY: AppRole[] = ['operator', 'supervisor', 'org_admin', 'superadmin'];

export function useUserRole() {
  const { user } = useAuth();
  const [role, setRole] = useState<AppRole | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    if (!user) {
      setRole(null);
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }

    setLoading(true);

    supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', user.id)
      .then(({ data, error }) => {
        if (cancelled) return;

        if (error) {
          setRole('operator');
          setLoading(false);
          return;
        }

        const assignedRoles = data?.map(({ role }) => role) ?? [];
        const highestRole = [...ROLE_PRIORITY]
          .reverse()
          .find((candidate) => assignedRoles.includes(candidate)) ?? 'operator';

        setRole(highestRole);
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  const isSuperadmin = role === 'superadmin';
  const isAdmin = role === 'org_admin' || isSuperadmin;
  const isSupervisor = role === 'supervisor' || isAdmin;

  return { role, loading, isSuperadmin, isAdmin, isSupervisor };
}
